import { FronteggContext } from '@frontegg/rest-api';
import { AxiosAmbient } from 'src/lib';
import { showToastError } from 'src/utilities/errortoast';
import { Axios, AxiosIntegrations } from 'src/lib';
// import { showToastSuccess } from 'src/utils/showToast';

export const linkDocumentToEncounter = async (encounterId, documentIds) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.post(
      `/encounters/${encounterId}/link_document`,
      { document_ids: documentIds },
      {
        headers,
      }
    );
    const response = result.data;
    if (result?.status < 300) {
      return response;
    }
  } catch (error) {
    showToastError('Failed to link document to encounter');
    throw error;
  }
};

export const unlinkDocumentFromEncounter = async (encounterId, documentIds) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.post(
      `/encounters/${encounterId}/unlink_document`,
      { document_ids: documentIds },
      {
        headers,
      }
    );
    const response = result.data;
    if (result?.status < 300) {
      return response;
    }
  } catch (error) {
    showToastError('Failed to unlink document from encounter');
    throw error;
  }
};

export const linkDocumentToAppointment = async (
  appointmentUuid,
  documentIds
) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.post(
      `/appointments/${appointmentUuid}/link_document`,
      { document_ids: documentIds },
      {
        headers,
      }
    );
    const response = result.data;
    if (result?.status < 300) {
      return response;
    }
  } catch (error) {
    showToastError('Failed to link document to appointment');
    throw error;
  }
};

export const unlinkDocumentFromAppointment = async (
  appointmentUuid,
  documentIds
) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.post(
      `/appointments/${appointmentUuid}/unlink_document`,
      { document_ids: documentIds },
      {
        headers,
      }
    );
    const response = result.data;
    if (result?.status < 300) {
      return response;
    }
  } catch (error) {
    showToastError('Failed to unlink document from appointment');
    throw error;
  }
};

export const createPatientDocument = async (
  encounterId,
  appointmentUuid,
  data
) => {
  const { file, ...rest } = data;

  try {
    const formData = new FormData();
    Object.entries(rest).forEach(([key, value]) => {
      formData.append(key, value);
    });

    if (file) {
      formData.append('file', file);
    }

    const headers = {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.post('/patient_document', formData, {
      headers,
    });
    const response = result.data;
    if (result?.status < 300) {
      if (data.source === 'pre_chart_history') {
        await linkDocumentToEncounter(encounterId, [
          response.patient_document_id,
        ]);
      } else if (data.source === 'vca_patient_history') {
        await linkDocumentToAppointment(appointmentUuid, [
          response.patient_document_id,
        ]);
      }
      return response;
    }
  } catch (error) {
    showToastError('Failed to create patient document');
    throw error;
  }
};

export const updatePatientDocument = async (documentId, data) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.put(
      `/patient_document/${documentId}`,
      data,
      {
        headers,
      }
    );
    const response = result.data;
    if (result?.status < 300) {
      return response;
    }
  } catch (error) {
    showToastError('Failed to update patient document');
    throw error;
  }
};

export const getEncounterDocuments = async (encounterId) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.get(
      `/encounters/${encounterId}/documents`,
      {
        headers,
      }
    );
    const response = result.data;
    if (result?.status < 300) {
      return response.encounter_documents;
    }
  } catch (error) {
    showToastError('Failed to fetch encounter documents');
    throw error;
  }
};

export const getAppointmentDocuments = async (appointmentUuid) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.get(
      `/appointments/${appointmentUuid}/documents`,
      {
        headers,
      }
    );
    const response = result.data;
    if (result?.status < 300) {
      return response.documents;
    }
  } catch (error) {
    showToastError('Failed to fetch appointment documents');
    throw error;
  }
};

export const fetchLastEhrAppointments = async ({
  patientId,
  practitionerId,
  appointmentId,
}) => {
  if (!patientId || !practitionerId) {
    const errorMsg =
      'Missing required parameters for fetching EHR appointments.';
    showToastError(errorMsg);
    throw new Error(errorMsg);
  }

  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const url = `/ehr/appointments`;
    const result = await Axios.get(url, {
      params: {
        count: 1,
        patient_id: patientId,
        practitioner_id: practitionerId,
        appointment_id: appointmentId,
      },
      headers: headers,
    });

    if (result.status === 200 && result.data) {
      return result.data;
    } else {
      const errorMsg =
        'Received unexpected data format from EHR appointments API.';
      // showToastError(errorMsg);
      throw new Error(errorMsg);
    }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      'Failed to fetch recent appointments from EHR.';
    // showToastError(message);
    throw error;
  }
};

export const changeAppointmentStatus = async ({
  appointmentIds,
  status,
  parent_appointment_ehr_id,
  practitioner_id,
  encounter_id,
  notes_content,
}) => {
  if (!appointmentIds || !status || !parent_appointment_ehr_id) {
    const errorMsg =
      'Missing required parameters for changing appointment status.';
    showToastError(errorMsg);
    throw new Error(errorMsg);
  }

  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const url = `/ehr/appointments/notes/status`;
    const result = await Axios.post(
      url,
      {
        appointment_ids: appointmentIds,
        status: status,
        parent_appointment_ehr_id: parent_appointment_ehr_id,
        practitioner_id: practitioner_id,
        encounter_id: encounter_id,
        notes_content: notes_content,
      },
      {
        headers: headers,
      }
    );

    if (result.status === 200 && result.data) {
      return result.data;
    } else {
      const errorMsg =
        'Received unexpected data format from EHR appointments API.';
      // showToastError(errorMsg);
      throw new Error(errorMsg);
    }
  } catch (error) {
    const message =
      error.response?.data?.message ||
      'Failed to fetch recent appointments from EHR.';
    // showToastError(message);
    throw error;
  }
};

export const regenerateEncounterNotes = async ({
  encounterId,
  clinical_note_id,
}) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const url = `/prechart/re-generate`;
    const result = await AxiosAmbient.post(
      url,
      {
        encounter_id: encounterId,
        clinical_note_id: clinical_note_id,
      },
      {
        headers: headers,
      }
    );

    if (result.status === 200 && result.data) {
      return result.data;
    } else {
      const errorMsg =
        'Received unexpected data format from regenerate encounter notes API.';
      // showToastError(errorMsg);
      throw new Error(errorMsg);
    }
  } catch (error) {
    const message =
      error.response?.data?.message || 'Failed to regenerate encounter notes.';
    // showToastError(message);
    throw error;
  }
};
